import React from 'react';
import { X, Plus, Minus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';

export const CartSidebar = () => {
  const { cart, isOpen, setIsOpen, updateQuantity, removeFromCart, loading } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsOpen(false);
    if (!user) {
      navigate('/login?redirect=/cart');
    } else {
      navigate('/cart');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-md bg-cream-50 p-0 flex flex-col">
        <SheetHeader className="px-6 py-6 border-b border-cream-200">
          <SheetTitle className="font-heading text-2xl text-leather-900">
            Your Cart ({cart.items.length})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="spinner"></div>
            </div>
          ) : cart.items.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-leather-400 mb-6">Your cart is empty</p>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/shop/belts');
                }}
                className="btn-secondary"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.items.map((item) => (
                <div key={item.product_id} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-20 h-20 bg-cream-100 flex-shrink-0">
                    <img
                      src={item.product?.images?.[0] || '/placeholder.jpg'}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-leather-900 text-sm mb-1">
                      {item.product?.name}
                    </h4>
                    <p className="text-leather-600 text-sm mb-2">
                      ${item.product?.price?.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center border border-cream-200 hover:border-leather-900"
                        data-testid={`decrease-qty-${item.product_id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center border border-cream-200 hover:border-leather-900"
                        data-testid={`increase-qty-${item.product_id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="ml-auto text-leather-400 hover:text-red-500"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.items.length > 0 && (
          <div className="px-6 py-6 border-t border-cream-200 bg-cream-100">
            <div className="flex justify-between mb-4">
              <span className="text-leather-600">Subtotal</span>
              <span className="font-medium text-leather-900">${cart.total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-leather-400 mb-4">
              Shipping and taxes calculated at checkout
            </p>
            <button
              onClick={handleCheckout}
              className="w-full btn-primary"
              data-testid="checkout-btn"
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
